// Rezept-Verwaltung: Liste aller Rezepte + Edit-Formular.
// Lädt die Daten live vom Bertsy-Backend (nicht via Vite-Glob), damit nach dem
// Speichern auch sofort der aktuelle Stand sichtbar ist.

import { useEffect, useState } from 'react';
import { deleteRezept, listRezepte, getRezept, saveRezept, type RezeptFull, type RezeptSummary, type Tipp } from '../api';
import regionsJson from '../../../../content/body/regions.json';

interface Region {
  id: string;
  name: string;
}
const ALLE_REGIONEN: Region[] = (regionsJson as any).regionen ?? [];

const KATEGORIEN: Array<{ key: Tipp['kategorie']; label: string }> = [
  { key: 'hausmittel', label: 'Hausmittel' },
  { key: 'verhalten', label: 'Verhalten' },
  { key: 'ernaehrung', label: 'Ernährung' },
  { key: 'rezeptfrei', label: 'Rezeptfreie Mittel aus der Apotheke' },
  { key: 'ruhe', label: 'Ruhe & Schlaf' },
];

const SCHWERE_COLOR: Record<string, string> = {
  leicht: '#7BB592',
  mittel: '#E99754',
  schwer: '#D9534F',
};

export default function RezeptEditor() {
  const [view, setView] = useState<'list' | 'edit' | 'new'>('list');
  const [editingId, setEditingId] = useState<string | null>(null);

  if (view === 'edit' && editingId) {
    return (
      <EditForm
        id={editingId}
        onBack={() => {
          setView('list');
          setEditingId(null);
        }}
      />
    );
  }
  if (view === 'new') {
    return (
      <NewRezeptForm
        onCreate={(id) => {
          setEditingId(id);
          setView('edit');
        }}
        onCancel={() => setView('list')}
      />
    );
  }
  return (
    <RezeptListView
      onSelect={(id) => {
        setEditingId(id);
        setView('edit');
      }}
      onNew={() => setView('new')}
    />
  );
}

// ─── Neues Rezept anlegen ────────────────────────────────────────────

function NewRezeptForm({
  onCreate,
  onCancel,
}: {
  onCreate: (id: string) => void;
  onCancel: () => void;
}) {
  const [titel, setTitel] = useState('');
  const [id, setId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  // Automatische ID-Generierung aus Titel (kann manuell überschrieben werden)
  const slugify = (s: string): string =>
    s.toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);

  const onTitelChange = (v: string) => {
    setTitel(v);
    if (id === '' || id === slugify(titel)) {
      setId(slugify(v));
    }
  };

  const onAnlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!titel.trim()) { setError('Titel ist erforderlich.'); return; }
    if (!id.trim()) { setError('ID ist erforderlich.'); return; }
    if (!/^[a-z0-9-]+$/.test(id)) {
      setError('ID darf nur kleine Buchstaben, Zahlen und Bindestriche enthalten.');
      return;
    }
    setChecking(true);
    try {
      // Existenz-Check: gibt es bereits ein Rezept mit dieser ID?
      try {
        await getRezept(id);
        setError(`Es existiert bereits ein Rezept mit der ID "${id}". Wähle eine andere ID.`);
        setChecking(false);
        return;
      } catch (err: any) {
        // 404 = existiert noch nicht → OK
        if (!String(err.message).includes('not_found') && !String(err.message).includes('404')) {
          throw err;
        }
      }

      // Leeres Rezept mit Minimal-Pflichtfeldern speichern
      await saveRezept(id, {
        id,
        titel: titel.trim(),
        kurzbeschreibung: '',
        regionen: [],
        schlagwoerter: [],
        schweregrad: 'leicht',
        tipps: [],
        warnsignale: [],
        arzt_aufsuchen_wenn: '',
        krankmeldung_sinnvoll: false,
        letzte_aktualisierung: new Date().toISOString().slice(0, 10),
        quelle: 'Bertsy Beratungs-Rezept',
      });
      onCreate(id);
    } catch (err: any) {
      setError(err.message ?? 'Speichern fehlgeschlagen');
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <h1 className="page-title">Neues Rezept anlegen</h1>
      <p className="page-sub">
        Gib zuerst Titel und ID an. Anschließend kannst du im vollständigen Editor alle Inhalte ergänzen.
      </p>

      <form onSubmit={onAnlegen} className="card" style={{ maxWidth: 600 }}>
        <div className="field">
          <label>Titel *</label>
          <input
            type="text"
            value={titel}
            onChange={(e) => onTitelChange(e.target.value)}
            placeholder="z. B. Reizdarm-Syndrom"
            required
            autoFocus
          />
        </div>

        <div className="field">
          <label>ID (URL-tauglich) *</label>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value.toLowerCase())}
            placeholder="z. B. reizdarm-syndrom"
            pattern="[a-z0-9-]+"
            required
          />
          <p style={{ fontSize: 11, color: '#5F6B68', margin: '4px 0 0' }}>
            Nur kleine Buchstaben, Zahlen und Bindestriche. Wird automatisch aus dem Titel generiert,
            kann aber überschrieben werden. Nach dem Anlegen NICHT mehr änderbar!
          </p>
        </div>

        {error && (
          <div style={{
            padding: 10, background: '#FFEBEE', borderLeft: '3px solid #C62828',
            color: '#C62828', fontSize: 13, marginTop: 12,
          }}>
            {error}
          </div>
        )}

        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Abbrechen</button>
          <button type="submit" className="btn btn-primary" disabled={checking}>
            {checking ? 'Lege an…' : 'Anlegen und bearbeiten'}
          </button>
        </div>
      </form>
    </>
  );
}

// ─── Liste ───────────────────────────────────────────────────────────

function RezeptListView({ onSelect, onNew }: { onSelect: (id: string) => void; onNew: () => void }) {
  const [rezepte, setRezepte] = useState<RezeptSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    listRezepte()
      .then((r) => setRezepte(r.rezepte))
      .catch((e: Error) => setErr(e.message));
  }, []);

  if (err) {
    return (
      <>
        <h1 className="page-title">Rezepte bearbeiten</h1>
        <div className="card" style={{ color: '#D9534F' }}>
          Fehler beim Laden: <code>{err}</code>
          <br />
          <br />
          Läuft der Backend-Server? <code>cd backend/server && npm run dev</code>
        </div>
      </>
    );
  }
  if (!rezepte) {
    return (
      <>
        <h1 className="page-title">Rezepte bearbeiten</h1>
        <p className="page-sub">Wird geladen…</p>
      </>
    );
  }

  const gefiltert = q
    ? rezepte.filter((r) =>
        (r.id + ' ' + r.titel).toLowerCase().includes(q.toLowerCase()),
      )
    : rezepte;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12 }}>
        <div>
          <h1 className="page-title" style={{ margin: '0 0 4px' }}>Rezepte bearbeiten</h1>
          <p className="page-sub" style={{ margin: 0 }}>
            {rezepte.length} Rezepte. Änderungen sind sofort in der Mobile-App und im Embed-Widget sichtbar
            (beim nächsten App-Start).
          </p>
        </div>
        <button className="btn btn-primary" onClick={onNew}>+ Neues Rezept</button>
      </div>

      <div className="card">
        <div className="field">
          <input
            type="search"
            placeholder="Rezept suchen…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#5F6B68', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
              <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8' }}>Titel</th>
              <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 90 }}>Schwere</th>
              <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 80 }}>Tipps</th>
              <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 80 }}>Warn</th>
              <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 200 }}></th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid #DDE9E8' }}>
                <td style={{ padding: '12px 8px' }}>
                  <div style={{ fontWeight: 600, color: '#1D1D1B' }}>{r.titel}</div>
                  <code style={{ fontSize: 11, color: '#5F6B68' }}>{r.id}</code>
                </td>
                <td style={{ padding: '12px 8px' }}>
                  {r.schweregrad && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: 999,
                        background: SCHWERE_COLOR[r.schweregrad] ?? '#999',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {r.schweregrad}
                    </span>
                  )}
                </td>
                <td style={{ padding: '12px 8px', color: '#5F6B68' }}>{r.tipps}</td>
                <td style={{ padding: '12px 8px', color: '#5F6B68' }}>{r.warnsignale}</td>
                <td style={{ padding: '12px 8px', display: 'flex', gap: 6 }}>
                  <button className="btn btn-primary" onClick={() => onSelect(r.id)}>
                    Bearbeiten
                  </button>
                  <button
                    className="btn btn-ghost"
                    style={{ color: '#C62828', borderColor: '#C62828' }}
                    onClick={async () => {
                      if (!confirm(`Rezept "${r.titel}" wirklich löschen?\n\nDas Rezept verschwindet sofort in App, Praxis-Dashboard und Widget. Die Datei wird vom Server entfernt — kein Zurück.`)) return;
                      try {
                        await deleteRezept(r.id);
                        setRezepte((rs) => (rs ?? []).filter((x) => x.id !== r.id));
                      } catch (err: any) {
                        alert('Fehler: ' + (err.message ?? err));
                      }
                    }}
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Editor ──────────────────────────────────────────────────────────

function EditForm({ id, onBack }: { id: string; onBack: () => void }) {
  const [rezept, setRezept] = useState<RezeptFull | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    getRezept(id)
      .then((r) => setRezept(r.rezept))
      .catch((e: Error) => setErr(e.message));
  }, [id]);

  if (err) {
    return (
      <>
        <button className="btn btn-ghost" onClick={onBack}>← Zurück</button>
        <div className="card" style={{ color: '#D9534F', marginTop: 16 }}>Fehler: {err}</div>
      </>
    );
  }
  if (!rezept) {
    return (
      <>
        <button className="btn btn-ghost" onClick={onBack}>← Zurück</button>
        <p className="page-sub" style={{ marginTop: 16 }}>Wird geladen…</p>
      </>
    );
  }

  const setField = <K extends keyof RezeptFull>(key: K, value: RezeptFull[K]) =>
    setRezept((r) => (r ? { ...r, [key]: value } : r));

  const onSave = async () => {
    if (!rezept) return;
    setSaving(true);
    setErr(null);
    try {
      await saveRezept(id, rezept);
      setSavedAt(new Date());
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <button className="btn btn-ghost" onClick={onBack}>← Zurück zur Liste</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {savedAt && (
            <span style={{ color: '#7BB592', fontSize: 13, fontWeight: 600 }}>
              ✓ Gespeichert ({savedAt.toLocaleTimeString('de-DE')})
            </span>
          )}
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Speichern…' : '💾 Speichern'}
          </button>
        </div>
      </div>

      <h1 className="page-title">{rezept.titel}</h1>
      <p className="page-sub">
        ID: <code>{rezept.id}</code> · {rezept.tipps.length} Tipps · {rezept.warnsignale.length} Warnsignale
      </p>

      {err && <div className="card" style={{ color: '#D9534F', marginBottom: 16 }}>Fehler: {err}</div>}

      <div className="card">
        <div className="section-title" style={{ marginTop: 0 }}>Grunddaten</div>

        <div className="field">
          <label>Titel</label>
          <input
            type="text"
            value={rezept.titel}
            onChange={(e) => setField('titel', e.target.value)}
          />
        </div>

        <div className="field">
          <label>Kurzbeschreibung</label>
          <textarea
            rows={3}
            value={rezept.kurzbeschreibung ?? ''}
            onChange={(e) => setField('kurzbeschreibung', e.target.value)}
          />
        </div>

        <div className="field">
          <label>Schweregrad</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['leicht', 'mittel', 'schwer'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={rezept.schweregrad === s ? 'btn btn-primary' : 'btn btn-ghost'}
                style={
                  rezept.schweregrad === s
                    ? { background: SCHWERE_COLOR[s], color: '#fff', border: 0 }
                    : undefined
                }
                onClick={() => setField('schweregrad', s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label>Körper-Regionen (Zuordnung zum Körper-Tab in der App)</label>
          <p style={{ fontSize: 12, color: '#5F6B68', margin: '0 0 8px 0' }}>
            Klick = zuweisen / entfernen. Aktive Regionen sind grün. Wenn keine Region zugewiesen ist,
            erscheint das Rezept nicht im Körper-Tab — nur über die Suche / Home.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ALLE_REGIONEN.map((r) => {
              const active = rezept.regionen.includes(r.id);
              return (
                <button
                  key={r.id}
                  type="button"
                  className={active ? 'btn btn-primary' : 'btn btn-ghost'}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={() => {
                    setField(
                      'regionen',
                      active
                        ? rezept.regionen.filter((x) => x !== r.id)
                        : [...rezept.regionen, r.id],
                    );
                  }}
                >
                  {active ? '✓ ' : ''}{r.name}
                </button>
              );
            })}
          </div>
          {rezept.regionen.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#5F6B68' }}>
                {rezept.regionen.length} Zuordnung{rezept.regionen.length === 1 ? '' : 'en'}:
              </span>
              {rezept.regionen.map((id) => {
                const r = ALLE_REGIONEN.find((x) => x.id === id);
                return (
                  <span
                    key={id}
                    style={{
                      background: '#00A099', color: '#fff',
                      padding: '2px 10px 2px 10px', borderRadius: 999, fontSize: 11,
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {r?.name ?? id}
                    <button
                      type="button"
                      onClick={() => setField('regionen', rezept.regionen.filter((x) => x !== id))}
                      title="Zuordnung entfernen"
                      style={{
                        background: 'transparent', border: 0, color: '#fff',
                        cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0,
                      }}
                    >×</button>
                  </span>
                );
              })}
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 11, color: '#D9534F', padding: '2px 8px' }}
                onClick={() => setField('regionen', [])}
              >
                Alle entfernen
              </button>
            </div>
          )}
        </div>

        <div className="field">
          <label>Schlagwörter (Komma-getrennt)</label>
          <input
            type="text"
            value={rezept.schlagwoerter.join(', ')}
            onChange={(e) =>
              setField(
                'schlagwoerter',
                e.target.value
                  .split(',')
                  .map((s) => s.trim().toLowerCase())
                  .filter(Boolean),
              )
            }
          />
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="section-title" style={{ marginTop: 0 }}>Tipps</div>
        {KATEGORIEN.map((k) => (
          <KategorieBlock
            key={k.key}
            kategorie={k.key}
            label={k.label}
            tipps={rezept.tipps.filter((t) => t.kategorie === k.key)}
            onChange={(neueListe) => {
              const andere = rezept.tipps.filter((t) => t.kategorie !== k.key);
              setField('tipps', [...andere, ...neueListe]);
            }}
          />
        ))}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="section-title" style={{ marginTop: 0 }}>Sofort zum Arzt bei (Warnsignale)</div>
        <StringListEditor
          items={rezept.warnsignale}
          onChange={(items) => setField('warnsignale', items)}
          placeholder="z. B. Fieber über 39 °C"
        />

        <div className="field" style={{ marginTop: 16 }}>
          <label>Arzt aufsuchen, wenn… (Fließtext)</label>
          <textarea
            rows={3}
            value={rezept.arzt_aufsuchen_wenn ?? ''}
            onChange={(e) => setField('arzt_aufsuchen_wenn', e.target.value)}
            placeholder="z. B. Bei länger anhaltenden Beschwerden oder beim Auftreten der oben genannten Warnsignale einen Arzt aufsuchen."
          />
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 0 }}>
          <div className="section-title" style={{ margin: 0 }}>Komplettes Rezept (HTML)</div>
          <span style={{ fontSize: 12, color: '#5F6B68' }}>
            {(rezept.inhalt_html ?? '').length} Zeichen
          </span>
        </div>
        <p style={{ fontSize: 12, color: '#5F6B68', margin: '4px 0 12px 0' }}>
          Dieser Inhalt wird in der Mobile-App via „Komplettes Rezept ansehen" angezeigt. Wenn leer,
          erscheint der Button in der App nicht (sondern nur die Tipps + Warnsignale). Erlaubt sind
          einfache HTML-Tags wie <code>&lt;h2&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;ul&gt;</code>,
          <code>&lt;li&gt;</code>, <code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code>, <code>&lt;br&gt;</code>.
        </p>
        <textarea
          rows={14}
          value={rezept.inhalt_html ?? ''}
          onChange={(e) => setField('inhalt_html', e.target.value)}
          placeholder={'<h2>Was steckt dahinter?</h2>\n<p>Eine kurze Erklärung der Beschwerde …</p>\n\n<h2>Was Sie selbst tun können</h2>\n<ul>\n  <li>Tipp eins</li>\n  <li>Tipp zwei</li>\n</ul>'}
          style={{
            width: '100%', padding: 10, fontSize: 13,
            border: '1px solid #DDE9E8', borderRadius: 6,
            fontFamily: 'ui-monospace,Menlo,Consolas,monospace',
            minHeight: 240,
            resize: 'vertical',
          }}
        />
        {(rezept.inhalt_html ?? '').trim().length > 0 && (
          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: '#00A099', fontWeight: 600 }}>
              👁 Vorschau (so wird es in der App gerendert)
            </summary>
            <div
              style={{
                marginTop: 10, padding: 16,
                background: '#F4FAFA', border: '1px solid #DDE9E8',
                borderRadius: 6, fontSize: 14, lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: rezept.inhalt_html ?? '' }}
            />
          </details>
        )}
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: 12, color: '#D9534F' }}
            onClick={() => {
              if (confirm('Komplettes Rezept (inhalt_html) komplett löschen?')) {
                setField('inhalt_html', undefined);
              }
            }}
          >
            🗑 Komplettes Rezept leeren
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button className="btn btn-primary" onClick={onSave} disabled={saving} style={{ minWidth: 200 }}>
          {saving ? 'Speichern…' : '💾 Speichern'}
        </button>
      </div>
    </>
  );
}

// ─── Dynamische Listen ───────────────────────────────────────────────

function KategorieBlock({
  kategorie,
  label,
  tipps,
  onChange,
}: {
  kategorie: Tipp['kategorie'];
  label: string;
  tipps: Tipp[];
  onChange: (t: Tipp[]) => void;
}) {
  const add = () =>
    onChange([...tipps, { kategorie, titel: '', beschreibung: '' }]);
  const remove = (i: number) => onChange(tipps.filter((_, idx) => idx !== i));
  const update = (i: number, field: 'titel' | 'beschreibung', value: string) =>
    onChange(tipps.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));

  return (
    <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #DDE9E8' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ color: '#00A099', textTransform: 'uppercase', letterSpacing: 1, fontSize: 12 }}>
          {label}
        </strong>
        <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} onClick={add}>
          + Tipp hinzufügen
        </button>
      </div>
      {tipps.length === 0 ? (
        <div style={{ color: '#5F6B68', fontSize: 13, fontStyle: 'italic' }}>Keine Tipps in dieser Kategorie.</div>
      ) : (
        tipps.map((t, i) => (
          <div key={i} style={{ background: '#F2F8F8', borderRadius: 6, padding: 10, marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
              <button
                type="button"
                onClick={() => remove(i)}
                style={{ background: 'transparent', border: 0, color: '#D9534F', cursor: 'pointer', fontSize: 12 }}
              >
                🗑 Entfernen
              </button>
            </div>
            <input
              type="text"
              placeholder="Titel (optional)"
              value={t.titel}
              onChange={(e) => update(i, 'titel', e.target.value)}
              style={{ width: '100%', marginBottom: 4, padding: 6, fontSize: 13 }}
            />
            <textarea
              placeholder="Beschreibung"
              value={t.beschreibung}
              rows={2}
              onChange={(e) => update(i, 'beschreibung', e.target.value)}
              style={{ width: '100%', padding: 6, fontSize: 13 }}
            />
          </div>
        ))
      )}
    </div>
  );
}

function StringListEditor({
  items,
  onChange,
  placeholder,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}) {
  const add = () => onChange([...items, '']);
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i: number, v: string) =>
    onChange(items.map((s, idx) => (idx === i ? v : s)));

  return (
    <>
      {items.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input
            type="text"
            value={s}
            placeholder={placeholder}
            onChange={(e) => update(i, e.target.value)}
            style={{ flex: 1, padding: 8 }}
          />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => remove(i)}
            style={{ color: '#D9534F' }}
          >
            🗑
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost" onClick={add} style={{ fontSize: 13 }}>
        + Warnsignal hinzufügen
      </button>
    </>
  );
}
